/**
 * The workout's device settings (`D209`): the warm-up calculator's method
 * and rounding, the plate calculator's bar and plates, the warm-up rest, the
 * rest alarm's sound, volume and vibration, whether the screen stays on, and
 * whether a set and a record make their sound (`D243`).
 *
 * ── THE DEVICE'S, NOT THE ACCOUNT'S ────────────────────────────────────────
 * A gym's plates and a phone's speaker belong to where the person trains
 * with that phone, not to the person — a second phone at a second gym is a
 * second answer. So nothing here goes to the server; it is ONE JSON value in
 * Capacitor Preferences (`src/native/storage.ts`), the store every other
 * device preference uses.
 *
 * ── READ FIELD BY FIELD, NEVER ALL OR NOTHING ──────────────────────────────
 * A value written by a newer build, or one field a hand edit broke, must not
 * reset every other choice: each field is parsed on its own and falls back
 * to its default alone. The defaults are the behaviour before `D209` — sound
 * on, vibration on, the screen kept awake — plus a 45 s warm-up rest, and
 * the set's own sounds on (`D243`): they play at the rest's volume, so a
 * phone set to "Mudo" stays silent without a second switch.
 *
 * The store is synchronous to READ (the rest alert and the actions ask it
 * mid-tick) and asynchronous to load: until the stored value arrives the
 * defaults answer, which is the behaviour of a first launch.
 */

import type { WeightUnit } from "@repo/contract";
import { z } from "zod";
import { createStore, type StoreApi } from "zustand/vanilla";
import {
  type BarKind,
  DEFAULT_LB_PLATES,
  DEFAULT_PLATES,
  LB_PLATE_SIZES,
  PLATE_SIZES,
} from "../plates/plate-math";
import { DEFAULT_WARMUP_REST_SECONDS } from "../store/session-model";
import { WARMUP_METHODS, type WarmupMethod } from "../warmup/warmup-math";

export type WarmupRoundingChoice = "auto" | "dumbbell" | "plates";
export type RestSound = "bell" | "default" | "success";
export type RestVolume = "high" | "low" | "medium" | "off";

export interface WorkoutPrefs {
  readonly bar: BarKind;
  /** The bar's weight when `bar` is `custom`. */
  readonly barCustomKg: number;
  /**
   * The same, in a pound gym (`D266`). Its own field and not a conversion:
   * a person who switches unit is in a different gym's numbers, and a 20 kg
   * custom bar read back as "44,09 lb" is a bar nobody owns.
   */
  readonly barCustomLb: number;
  /** Whether the screen stays on while a workout runs (B11). */
  readonly keepAwake: boolean;
  /** The plate sizes this gym has, heaviest first (B3). */
  readonly plates: readonly number[];
  /** The pound plates this gym has, heaviest first (`D266`). */
  readonly platesLb: readonly number[];
  readonly restSound: RestSound;
  readonly restVolume: RestVolume;
  /** A tick per logged set and a fanfare per record (`D243`). */
  readonly setSounds: boolean;
  /** A buzz when the rest ends in the foreground (B8). */
  readonly vibrate: boolean;
  readonly warmupMethod: WarmupMethod;
  /** The rest after a warm-up set, seconds (B7). */
  readonly warmupRestSeconds: number;
  readonly warmupRounding: WarmupRoundingChoice;
}

export const DEFAULT_WORKOUT_PREFS: WorkoutPrefs = {
  bar: "olympic",
  barCustomKg: 20,
  barCustomLb: 45,
  keepAwake: true,
  plates: DEFAULT_PLATES,
  platesLb: DEFAULT_LB_PLATES,
  restSound: "default",
  restVolume: "medium",
  setSounds: true,
  vibrate: true,
  warmupMethod: "classic",
  warmupRestSeconds: DEFAULT_WARMUP_REST_SECONDS,
  warmupRounding: "auto",
};

export const WORKOUT_PREFS_KEY = "mypocketgym.workout.prefs.v1";

/** The warm-up rest the settings offer, seconds. */
export const WARMUP_REST_RANGE = { max: 180, min: 15, step: 15 } as const;

const FIELDS = {
  bar: z.enum(["olympic", "women", "technique", "ez", "custom"]),
  barCustomKg: z.number().positive().max(100),
  barCustomLb: z.number().positive().max(220),
  keepAwake: z.boolean(),
  plates: z
    .array(z.number().refine((kg) => PLATE_SIZES.includes(kg)))
    .transform((kgs) => [...new Set(kgs)].sort((a, b) => b - a)),
  platesLb: z
    .array(z.number().refine((lb) => LB_PLATE_SIZES.includes(lb)))
    .transform((lbs) => [...new Set(lbs)].sort((a, b) => b - a)),
  restSound: z.enum(["default", "bell", "success"]),
  restVolume: z.enum(["off", "low", "medium", "high"]),
  setSounds: z.boolean(),
  vibrate: z.boolean(),
  warmupMethod: z.enum(WARMUP_METHODS as [WarmupMethod, ...WarmupMethod[]]),
  warmupRestSeconds: z
    .int()
    .min(WARMUP_REST_RANGE.min)
    .max(WARMUP_REST_RANGE.max),
  warmupRounding: z.enum(["auto", "dumbbell", "plates"]),
} satisfies { [K in keyof WorkoutPrefs]: z.ZodType };

/** The stored value, field by field (see the header). */
export function parseWorkoutPrefs(raw: string | null): WorkoutPrefs {
  if (raw === null) {
    return DEFAULT_WORKOUT_PREFS;
  }
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return DEFAULT_WORKOUT_PREFS;
  }
  if (typeof json !== "object" || json === null) {
    return DEFAULT_WORKOUT_PREFS;
  }
  const record = json as Record<string, unknown>;
  const out: Record<string, unknown> = { ...DEFAULT_WORKOUT_PREFS };
  for (const [key, schema] of Object.entries(FIELDS)) {
    const parsed = (schema as z.ZodType).safeParse(record[key]);
    if (parsed.success) {
      out[key] = parsed.data;
    }
  }
  return out as unknown as WorkoutPrefs;
}

/**
 * The plate calculator's gym in the person's unit (`D266`): the plates and
 * the custom bar, both IN that unit. Kilograms read the original fields, so
 * a person who never chose pounds sees exactly what they saw before.
 */
export function plateSetupOf(
  prefs: WorkoutPrefs,
  unit: WeightUnit
): { readonly barCustom: number; readonly plates: readonly number[] } {
  return unit === "lb"
    ? { barCustom: prefs.barCustomLb, plates: prefs.platesLb }
    : { barCustom: prefs.barCustomKg, plates: prefs.plates };
}

/** The patch that stores a plate set chosen in `unit`. */
export function platesPatch(
  unit: WeightUnit,
  plates: readonly number[]
): Partial<WorkoutPrefs> {
  const sorted = [...new Set(plates)].sort((a, b) => b - a);
  return unit === "lb" ? { platesLb: sorted } : { plates: sorted };
}

/** The patch that stores a custom bar weighed in `unit`. */
export function barCustomPatch(
  unit: WeightUnit,
  value: number
): Partial<WorkoutPrefs> {
  return unit === "lb" ? { barCustomLb: value } : { barCustomKg: value };
}

/** What the store needs of storage; Capacitor Preferences in the app. */
export interface PrefsStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}

export type WorkoutPrefsStore = StoreApi<WorkoutPrefs> & {
  /** Reads the stored value once; later calls answer the same promise. */
  load(): Promise<void>;
  /** Applies a change at once and writes it behind, in call order. */
  update(patch: Partial<WorkoutPrefs>): Promise<void>;
};

export function createWorkoutPrefsStore(
  storage: PrefsStorage
): WorkoutPrefsStore {
  const store = createStore<WorkoutPrefs>()(() => DEFAULT_WORKOUT_PREFS);
  let loading: Promise<void> | null = null;
  let written: Promise<unknown> = Promise.resolve();
  let loaded = false;
  // A change made before the stored value arrived wins over that field of
  // it: the person touched it, the disk only remembered.
  let early: Partial<WorkoutPrefs> = {};
  const load = (): Promise<void> => {
    loading ??= storage
      .getItem(WORKOUT_PREFS_KEY)
      .then((raw) => {
        store.setState({ ...parseWorkoutPrefs(raw), ...early }, true);
      })
      .catch(() => undefined)
      .finally(() => {
        loaded = true;
      });
    return loading;
  };
  return Object.assign(store, {
    load,
    update(patch: Partial<WorkoutPrefs>) {
      if (!loaded) {
        early = { ...early, ...patch };
      }
      store.setState(patch);
      // Written only once the stored value is in: a write before it would
      // put the defaults over every field the person did not just touch.
      written = written
        .then(load)
        .then(() =>
          storage.setItem(WORKOUT_PREFS_KEY, JSON.stringify(store.getState()))
        )
        .catch(() => undefined);
      return written.then(() => undefined);
    },
  });
}
